import React, { useState, useEffect, useMemo } from "react";
import Helmet from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import AddSystemAnnouncementModal from "components/Modals/AddSystemAnnouncement";
import CancelSystemAnnouncementModal from "components/Modals/cancelSystemAnnouncement";
import Table from "components/Table";
import Columns from "./Columns";
import { getSystemAnnouncementList } from "store/systemAnnouncement/actions";
import PermissionGate from "components/PermissionGate";
import usePermission from "hooks/usePermission";
import SystemAnnouncementModal from "components/Modals/systemAnnouncement/systemAnnouncementModal";
import FormikForm from "components/shared/FormikForm";

const InputFieldsForSearch = [
  {
    name: "search",
    type: "text",
    label: "",
    placeholder: "Search...",
  },
  {
    name: "type",
    type: "select",
    label: "",
    placeholder: "Select type",
    options: [
      { value: "Info", label: "Info" },
      { value: "Warning", label: "Warning" },
      { value: "Critical", label: "Critical" },
    ],
  },
  {
    name: "status",
    type: "select",
    label: "",
    placeholder: "Select status",
    options: [
      { value: "Active", label: "Active" },
      { value: "Completed", label: "Completed" },
      { value: "Cancelled", label: "Cancelled" },
    ],
  },
];
const initialValuesForFilter = {
  search: "",
  type: "",
  status: "",
};

const List = () => {
  const dispatch = useDispatch();

  const { loading, systemAnnouncementList, totalCount } = useSelector(
    ({ SystemAnnouncement }) => ({
      loading: SystemAnnouncement?.loading,
      systemAnnouncementList: SystemAnnouncement?.systemAnnouncementList,
      totalCount: SystemAnnouncement?.totalCount,
    }),
  );

  const { user } = useSelector((state) => state.Auth);

  const [columns, setColumns] = useState([]);
  const [announcementModalShow, setAnnouncementModalShow] = useState(false);
  const [addAnnouncementPop, setAddAnnouncementPop] = useState(false);
  const [cancelAnnouncementModalShow, setCancelAnnouncementModalShow] = useState(false);
  const [cancelAnnouncement, setCancelAnnouncement] = useState(null);
  const [id, setId] = useState(null);

  const [filter, setFilter] = useState({
    page: 1,
    limit: 20,
    order: -1,
    orderBy: "date_created_utc",
    search: "",
    type: "",
    status: ""
  });

  const { hasPermission } = usePermission();
  const permissions = useMemo(() => ({
    add: hasPermission("add_system_announcement"),
    view: hasPermission("view_system_announcement"),
  }), [hasPermission]);

  // ✅ API call
  useEffect(() => {
    getApiData();
  }, [filter]);

  // ✅ Columns setup
  useEffect(() => {
    setColumns(
      Columns({
        handleViewHandler,
        handleCancelHandler,
        permissions,
      })
    );
  }, [permissions.view, permissions.add]);

  const getApiData = () => {
    dispatch(getSystemAnnouncementList({ data: filter }));
  };

  const handleFilterForm = (info) => {
    setFilter({
      ...filter,
      page: 1,
      limit: 20,
      order: -1,
      orderBy: "date_created_utc",
      search: info.search,
      status: info.status,
      type: info.type,
    });
  };

  // ✅ View Handler
  const handleViewHandler = (info) => {
    let id = info?._id;

    // Handle ObjectId edge case
    if (typeof id === "object" && id !== null) {
      id = id?.$oid || id?.toString();
    }

    if (!id || typeof id !== "string") {
      console.error("Invalid ID:", id);
      return;
    }

    setId(id);
    setAnnouncementModalShow(true);
  };

  const handleCancelHandler = (info) => {
    let id = info?._id;

    // Handle ObjectId edge case
    if (typeof id === "object" && id !== null) {
      id = id?.$oid || id?.toString();
    }

    if (!id || typeof id !== "string") {
      console.error("Invalid ID:", id);
      return;
    }

    setCancelAnnouncement({
      _id: id,
      type: info?.type,
      isMaintenance: info?.isMaintenance,
    });

    setCancelAnnouncementModalShow(true);
  };

  return (
    <>
      {/* Add Modal */}
      {addAnnouncementPop && (
        <AddSystemAnnouncementModal
          show={addAnnouncementPop}
          loading={loading}
          handleClose={() => {
            setAddAnnouncementPop(false);
            getApiData(filter);
          }}
        />
      )}

      {/* Cancel Modal */}
      {cancelAnnouncementModalShow && (
        <CancelSystemAnnouncementModal
          show={cancelAnnouncementModalShow}
          handleClose={() =>  {
            setCancelAnnouncementModalShow(false)
            getApiData(filter);
          }}
          loading={loading}
          cancelAnnouncement={cancelAnnouncement}
        />
      )}

      {/* Loader */}
      {loading && (
        <div className="main-loader">
          <FadeLoader size={1000} />
        </div>
      )}

      {/* View Modal */}
      {announcementModalShow && (
        <SystemAnnouncementModal
          show={announcementModalShow}
          handleClose={() => {
            setAnnouncementModalShow(false);
            setId(null);
          }}
          title={"System Announcement Details"}
          id={id}
        />
      )}

      <Helmet title={"System Announcement"} />

      <section className="section">
        <div className="section-header">
          <h1>System Announcement</h1>
        </div>

        <div className="section-body">
          <div className="card">
            <div className="row justify-content-between">
              <div className="col-6">
                <div className="card-header">
                  <h2 className="section-title">System Announcement List</h2>
                </div>
              </div>

              <PermissionGate permission="add_system_announcement">
                <div className="col-6">
                  <div className="card-header d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-primary"
                      onClick={() => setAddAnnouncementPop(true)}
                    >
                      Add Announcement
                    </button>
                  </div>
                </div>
              </PermissionGate>
              <div className="col-12">
                <div className="card-header">
                  <FormikForm
                    initialValues={initialValuesForFilter}
                    onSubmit={handleFilterForm}
                    loading={loading}
                    inputFields={InputFieldsForSearch}
                    buttonText={"Search"}
                  />
                </div>
              </div>
            </div>
          </div>

          {!loading && systemAnnouncementList?.length > 0 && (
            <Table
              data={systemAnnouncementList}
              columns={columns}
              total={totalCount}
              // Pagination handling
              onTableChange={(type, { page, sizePerPage }) => {
                setFilter((prev) => ({
                  ...prev,
                  page,
                  limit: sizePerPage,
                }));
              }}
            />
          )}

          {!loading && systemAnnouncementList?.length === 0 && (
            <p style={{ textAlign: "center" }}>No System Announcement Found</p>
          )}
        </div>
      </section>
    </>
  );
};

export default List;
